class WHeader {
  constructor() {
    this.header = document.getElementById('w-header');
    if (!this.header) return;

    this.announcementBar = this.header.querySelector('.w-header__announcement');
    this.countdown = document.querySelector('[data-countdown-end]');
    this.megamenus = document.querySelectorAll('[data-megamenu]');
    this.megamenuTriggers = document.querySelectorAll('[data-megamenu-trigger]');
    this.megamenuOverlay = document.querySelector('[data-megamenu-overlay]');
    this.mobileDrawer = document.querySelector('[data-mobile-drawer]');
    this.mobileDrawerClose = document.querySelector('[data-mobile-drawer-close]');
    this.burgerButton = document.querySelector('[data-mobile-drawer-open]');

    this.activeMegamenu = null;
    this.activeTrigger = null;

    this.initStickyOffset();

    if (this.countdown) {
      this.initCountdown();
    }

    if (this.megamenus.length > 0 && this.megamenuTriggers.length > 0) {
      this.initMegamenu();
    }

    if (this.mobileDrawer) {
      this.initMobileDrawer();
    }
  }

  initStickyOffset() {
    const updateAnnouncementHeight = () => {
      if (this.announcementBar) {
        const height = this.announcementBar.offsetHeight;
        document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
      } else {
        document.documentElement.style.setProperty('--announcement-bar-height', '0px');
      }
    };

    updateAnnouncementHeight();

    if (window.ResizeObserver && this.announcementBar) {
      new ResizeObserver(updateAnnouncementHeight).observe(this.announcementBar);
    }

    window.addEventListener('resize', updateAnnouncementHeight);
  }

  initCountdown() {
    const endDate = this.countdown.getAttribute('data-countdown-end');
    if (!endDate) return;

    const endTime = new Date(endDate).getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = endTime - now;

      if (distance < 0) {
        this.countdown.style.display = 'none';
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      const daysEl = this.countdown.querySelector('[data-countdown-days]');
      const hoursEl = this.countdown.querySelector('[data-countdown-hours]');
      const minutesEl = this.countdown.querySelector('[data-countdown-minutes]');
      const secondsEl = this.countdown.querySelector('[data-countdown-seconds]');

      if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
      if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
      if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
      if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
    };

    updateCountdown();
    setInterval(updateCountdown, 1000);
  }

  initMegamenu() {
    let closeTimeout = null;

    const getMegamenuByIndex = (index) => {
      return document.querySelector(`[data-megamenu][data-menu-index="${index}"]`);
    };

    const closeAllMegamenus = () => {
      this.megamenus.forEach((menu) => menu.classList.remove('is-open'));
      this.megamenuTriggers.forEach((trigger) => trigger.classList.remove('is-active'));
      this.megamenuOverlay?.classList.remove('is-visible');
      this.activeMegamenu = null;
      this.activeTrigger = null;
    };

    const openMegamenu = (trigger) => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
        closeTimeout = null;
      }

      const menuIndex = trigger.getAttribute('data-menu-index');
      const megamenu = getMegamenuByIndex(menuIndex);

      if (!megamenu) return;

      closeAllMegamenus();

      megamenu.classList.add('is-open');
      trigger.classList.add('is-active');
      this.megamenuOverlay?.classList.add('is-visible');

      this.activeMegamenu = megamenu;
      this.activeTrigger = trigger;
    };

    const closeMegamenu = () => {
      closeTimeout = setTimeout(() => {
        closeAllMegamenus();
      }, 150);
    };

    const cancelClose = () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
        closeTimeout = null;
      }
    };

    this.megamenuTriggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', () => openMegamenu(trigger));
      trigger.addEventListener('mouseleave', closeMegamenu);

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const menuIndex = trigger.getAttribute('data-menu-index');
        const megamenu = getMegamenuByIndex(menuIndex);

        if (megamenu && megamenu.classList.contains('is-open')) {
          closeAllMegamenus();
        } else {
          openMegamenu(trigger);
        }
      });

      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const menuIndex = trigger.getAttribute('data-menu-index');
          const megamenu = getMegamenuByIndex(menuIndex);

          if (megamenu && megamenu.classList.contains('is-open')) {
            closeAllMegamenus();
          } else {
            openMegamenu(trigger);
          }
        }
        if (e.key === 'Escape') {
          closeAllMegamenus();
        }
      });
    });

    this.megamenus.forEach((megamenu) => {
      megamenu.addEventListener('mouseenter', cancelClose);
      megamenu.addEventListener('mouseleave', closeMegamenu);

      const categoryItems = megamenu.querySelectorAll('[data-category-index]');
      const subcategoryPanels = megamenu.querySelectorAll('[data-subcategory-index]');

      categoryItems.forEach((item) => {
        item.addEventListener('mouseenter', () => {
          const index = item.getAttribute('data-category-index');

          categoryItems.forEach((cat) => cat.classList.remove('is-active'));
          item.classList.add('is-active');

          subcategoryPanels.forEach((panel) => {
            if (panel.getAttribute('data-subcategory-index') === index) {
              panel.classList.add('is-visible');
            } else {
              panel.classList.remove('is-visible');
            }
          });
        });
      });
    });

    this.megamenuOverlay?.addEventListener('click', closeAllMegamenus);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activeMegamenu) {
        closeAllMegamenus();
      }
    });
  }

  initMobileDrawer() {
    const submenuTriggers = this.mobileDrawer.querySelectorAll('[data-mobile-submenu-trigger]');
    const submenuPanels = this.mobileDrawer.querySelectorAll('[data-mobile-submenu]');
    const backButtons = this.mobileDrawer.querySelectorAll('[data-mobile-submenu-back]');

    const level3Triggers = this.mobileDrawer.querySelectorAll('[data-mobile-level3-trigger]');
    const level3Panels = this.mobileDrawer.querySelectorAll('[data-mobile-level3]');
    const level3BackButtons = this.mobileDrawer.querySelectorAll('[data-mobile-level3-back]');

    const openDrawer = () => {
      this.mobileDrawer.classList.add('is-open');
      document.body.classList.add('menu-open');
    };

    const closeDrawer = () => {
      this.mobileDrawer.classList.remove('is-open');
      document.body.classList.remove('menu-open');
      submenuPanels.forEach((panel) => panel.classList.remove('is-open'));
      level3Panels.forEach((panel) => panel.classList.remove('is-open'));
    };

    const openSubmenu = (submenuId) => {
      const targetSubmenu = this.mobileDrawer.querySelector(`[data-mobile-submenu="${submenuId}"]`);
      if (targetSubmenu) {
        targetSubmenu.classList.add('is-open');
      }
    };

    const closeAllSubmenus = () => {
      submenuPanels.forEach((panel) => panel.classList.remove('is-open'));
      level3Panels.forEach((panel) => panel.classList.remove('is-open'));
    };

    const openLevel3 = (level3Id) => {
      const targetLevel3 = this.mobileDrawer.querySelector(`[data-mobile-level3="${level3Id}"]`);
      if (targetLevel3) {
        targetLevel3.classList.add('is-open');
      }
    };

    const closeAllLevel3 = () => {
      level3Panels.forEach((panel) => panel.classList.remove('is-open'));
    };

    this.burgerButton?.addEventListener('click', openDrawer);
    this.mobileDrawerClose?.addEventListener('click', closeDrawer);

    submenuTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const submenuId = trigger.getAttribute('data-mobile-submenu-trigger');
        openSubmenu(submenuId);
      });
    });

    backButtons.forEach((btn) => {
      btn.addEventListener('click', closeAllSubmenus);
    });

    level3Triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const level3Id = trigger.getAttribute('data-mobile-level3-trigger');
        openLevel3(level3Id);
      });
    });

    level3BackButtons.forEach((btn) => {
      btn.addEventListener('click', closeAllLevel3);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.mobileDrawer.classList.contains('is-open')) {
        const openLevel3Panel = this.mobileDrawer.querySelector('[data-mobile-level3].is-open');
        const openSubmenuPanel = this.mobileDrawer.querySelector('[data-mobile-submenu].is-open');
        if (openLevel3Panel) {
          closeAllLevel3();
        } else if (openSubmenuPanel) {
          closeAllSubmenus();
        } else {
          closeDrawer();
        }
      }
    });

    this.mobileDrawer.addEventListener('click', (e) => {
      if (e.target === this.mobileDrawer) {
        closeDrawer();
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new WHeader());
} else {
  new WHeader();
}
